<script>
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';

import { useAlert } from '../composables/useAlert';
import { useCurrentStudyStore } from '../store/dicom-web/current-study-store';
import { useAppState } from '../store/ocg-app-state';
import { resetStudyState } from '../store/resetAppState';
import AppSettingsDialog from './AppSettingsDialog.vue';
import AppSettingsItem from './AppSettingsItem.vue';
import EditUserProfile from './EditUserProfile.vue';
import HomeAction from './HomeAction.vue';
import OrthancLink from './OrthancLink.vue';
import SystemDialog from './SystemDialog.vue';
import VersionNumber from './VersionNumber.vue';

export default {
  components: {
    AppSettingsItem,
    EditUserProfile,
    VersionNumber,
    HomeAction,
    AppSettingsDialog,
    OrthancLink,
    SystemDialog,
  },
  setup() {
    const editProfileRef = ref(null);
    const appSettingsRef = ref(null);
    const systemDialogRef = ref(null);
    const appState = useAppState();
    const route = useRoute();

    const userName = computed(() => appState.currentUserName || 'Unknown User');

    const openEditProfile = (event) => {
      event.stopPropagation();
      event.preventDefault();
      if (editProfileRef.value) {
        editProfileRef.value.open();
      }
    };

    const openAppSettings = (event) => {
      event.stopPropagation();
      event.preventDefault();
      if (appSettingsRef.value) {
        appSettingsRef.value.open();
      }
    };

    const openSystemDialog = (event) => {
      event.stopPropagation();
      event.preventDefault();
      if (systemDialogRef.value) {
        systemDialogRef.value.open();
      }
    };

    const triggerLogOutUser = (event) => {
      event.stopPropagation();
      event.preventDefault();
      appState.logOut();
    };

    const currentStudyStore = useCurrentStudyStore();
    const alert = useAlert();

    const hasActiveStudy = computed(
      () => !!currentStudyStore.getStudyID && route.name !== 'Home',
    );

    const handleResetStudy = async (event) => {
      if (event) {
        event.stopPropagation();
        event.preventDefault();
      }
      const confirmed = await alert.confirm(
        'Are you sure you want to reset this study? This will clear all contours, calculations, and unsaved changes, restoring the study to its initial state.',
        'Reset Study',
        'warning',
      );
      if (!confirmed) return;

      try {
        resetStudyState();
        alert.info('Study has been reset to its initial state.');
      }
      catch (error) {
        console.error('Failed to reset study:', error);
        alert.error('Failed to reset study state.');
      }
    };

    return {
      appState,
      userName,
      openEditProfile,
      openAppSettings,
      openSystemDialog,
      editProfileRef,
      appSettingsRef,
      systemDialogRef,
      triggerLogOutUser,
      route,
      hasActiveStudy,
      handleResetStudy,
    };
  },
};
</script>

<template>
  <div>
    <v-menu>
      <template #activator="{ props }">
        <HomeAction label="App" icon="fa-solid fa-sliders" v-bind="props" />
      </template>

      <v-list class="dropdown -mr-1">
        <div v-if="userName" class="px-4 py-2">
          <h5>{{ userName }}</h5>
          <div class="default-separator my-2" />
        </div>

        <div v-if="route.name !== 'Home'">
          <AppSettingsItem icon="fa-solid fa-house" text="Home" to="/home" />
        </div>

        <div v-if="hasActiveStudy">
          <AppSettingsItem
            icon="fa-solid fa-arrow-rotate-left"
            text="Reset Study"
            @click="handleResetStudy"
          />
        </div>

        <div>
          <AppSettingsItem
            icon="fa-solid fa-user-edit"
            text="Edit Profile"
            @click="openEditProfile"
          />
        </div>
        <OrthancLink />

        <div
          v-if="
            appState.currentUserMode === 'admin'
              || appState.currentUserMode === 'superadmin'
          "
        >
          <AppSettingsItem
            icon="fa-solid fa-user-shield"
            text="Admin Settings"
            to="/admin"
          />
        </div>

        <div v-if="appState.currentUserMode === 'superadmin'">
          <AppSettingsItem
            icon="fa-solid fa-user-shield"
            text="Super Admin Settings"
            to="/superadmin"
          />
        </div>

        <AppSettingsItem
          icon="fa-solid fa-cog"
          text="App Settings"
          @click="openAppSettings"
        />

        <AppSettingsItem
          icon="fa-solid fa-cog"
          text="System Info"
          @click="openSystemDialog"
        />

        <AppSettingsItem
          icon="fa-solid fa-arrow-right-from-bracket"
          text="Sign Out"
          @click="triggerLogOutUser"
        />

        <div class="px-4 pt-4">
          <VersionNumber :margin-bot="5" :margin-right="10" />
        </div>
      </v-list>
    </v-menu>

    <EditUserProfile ref="editProfileRef" />
    <AppSettingsDialog ref="appSettingsRef" />
    <SystemDialog ref="systemDialogRef" />
  </div>
</template>

<style scoped>
.dropdown {
  width: 18rem;
  margin-top: 0.25rem;
}
.v-menu > .v-overlay__content > .v-list {
  background-color: var(--md-surface);
  border-radius: var(--container-radius);
  border: 1px solid var(--md-outline-variant);
  box-shadow: var(--md-elevation-1);
}
</style>
